import { createSelector } from 'reselect';
import _ from 'lodash';
import { formValueSelector, isSubmitting, getFormSyncWarnings } from 'redux-form';
import { formatPrice } from 'app/util';
import * as commonSelectors from 'app/common/selectors/common';
import * as shippingSelectors from 'app/selectors/shipping';
import { getStringsForPage, getMarkdownStringsForPage, getSectionForPageByIdAndType } from 'app/common/selectors/page';

const getFormValueSelector = () => formValueSelector('glowforgeOrderPage');
export const getProducts = state => _.get(state, 'glowforgeStore.products');
const getReferral = state => _.get(state, 'common.referral');
const airFilterProduct = state => _.get(state, 'glowforgeStore.products.air');

export const checkAirFilterByDefault = state => _.get(state, 'common.features.CHECK_AIR_FILTER_BY_DEFAULT') === true;
export const expressShippingEnabled = state => _.get(state, 'common.features.EXPRESS_SHIPPING') === true;

export const isAirFilterSelected = createSelector(
  getFormValueSelector,
  state => state,
  (selector, state) => selector(state, 'airFilter'),
);


export const getProId = createSelector(
  state => state,
  state => _.get(state, 'glowforgeStore.products.pro.id'),
);

export const getSelectedProductId = createSelector(
  getFormValueSelector,
  getProducts,
  state => state,
  (selector, products, state) => {
    const selected = selector(state, 'productPicker');
    if (selected) return selected;
    if (!_.get(products, 'pro.backordered')) return _.get(products, 'pro.id').toString();
    if (!_.get(products, 'plus.backordered')) return _.get(products, 'plus.id').toString();
    if (!_.get(products, 'basic.backordered')) return _.get(products, 'basic.id').toString();
    return null;
  },
);

export const getSelectedProduct = createSelector(
  getSelectedProductId,
  getProducts,
  (productId, products) => _.find(products, { id: parseInt(productId, 10) }),
);

export const getReferralInfo = createSelector(
  getReferral,
  getSelectedProduct,
  getProducts,
  (referral, selectedProduct) => {
    if (!referral) return { showReferralInfo: false };
    if (!referral.pl && !referral.isReferral) return { showReferralInfo: false };

    return {
      ...(_.get(selectedProduct, 'referral')),
      referredByName: referral.referrerName,
      isAffiliate: referral.isAffiliate,
      photoUrl: referral.photoUrl,
      code: referral.code,
      showReferralInfo: true,
      pl: referral.pl,
    };
  },
);

export const getOrderPageInfo = state => _.get(state, 'glowforgeStore.page.fields');

export const getOrderPageIcons = createSelector(
  getOrderPageInfo,
  (pageInfo) => {
    const icons = getSectionForPageByIdAndType(pageInfo, 'order-page-icons', 'section');
    return _.reduce(_.get(icons, 'content'), (acc, icon) => {
      const id = _.get(icon, 'fields.id');
      if (_.isNil(id)) {
        return acc;
      }
      acc[id] = _.get(icon, 'fields.svgAsset');
      return acc;
    }, {});
  },
);

export const getSubTotal = createSelector(
  getProducts,
  getSelectedProductId,
  isAirFilterSelected,
  getReferral,
  (products, selectedProductId, airFilter, referral) => {
    const product = Object.values(products).filter(p => p.id.toString() === selectedProductId)[0];
    let totalPrice = product.price;
    if (airFilter) {
      totalPrice += products.air.price;
    }

    if (referral && referral.isReferral) {
      totalPrice += _.get(product, 'referral.discount', 0);
    }

    return totalPrice;
  },
);

export const isSelectedBackordered = createSelector(
  getSelectedProduct,
  selectedProduct => _.get(selectedProduct, 'backordered'),
);

export const airFilterSelectedByDefault = createSelector(
  checkAirFilterByDefault,
  airFilterProduct,
  commonSelectors.isIpAddressInUS,
  (checkedByDefault, product, isUS) => checkedByDefault && isUS && product != null,
);

/**
 * We don't have data available that we can use to determine whether the options
 * selected on the order page are eligible for free shipping, or how much
 * shipping would cost for the selected items. For now we will hard code checks
 * for the user's country and product selection and then render a dynamic string
 * from Contentful if certain criteria are met.
 */
export const getShippingInfoMarkdown = createSelector(
  getOrderPageInfo,
  getSelectedProduct,
  isAirFilterSelected,
  commonSelectors.isIpAddressInUS,
  (
    orderPageInfo,
    selectedProduct,
    airFilterSelected,
    isIpAddressInUS,
  ) => {
    const defaultValue = '';

    if (!isIpAddressInUS) return defaultValue;

    const namesWithFreeShipping = ['pro', 'plus'];
    const freeShipping = !!(_.find(namesWithFreeShipping, name => (
      new RegExp(name, 'i').test(selectedProduct.name)
    )));

    if (!freeShipping) return defaultValue;

    const id = airFilterSelected
      ? 'free-shipping-with-filter'
      : 'free-shipping';

    const markdownStrings = getMarkdownStringsForPage(orderPageInfo);

    return markdownStrings[id] || defaultValue;
  },
);



// WEBPACK FOOTER //
// ./app/store/selectors/glowforge-order-page.js